import { useEffect, useMemo, useState } from "react";
import { IMarketFeedResponse, IMarketOutcomeFeedResponse } from "@finbackoffice/clientbff-client";
import { MarketStatus } from "@finbackoffice/enums";
import {
    IOutcomeData,
    MainMarketsMap,
    MainMarketsMapBC,
    SportTypes,
    SportTypesBC,
    TemplateIdsMap,
} from "@finbackoffice/fe-core";
import { findMainMarket, getColumnCount } from "../helpers";

enum OutcomeMagicNumbers {
    HOME = "1",
    DRAW = "X",
    AWAY = "2",
}

export interface IMainOddsState {
    homeOutcome: IMarketOutcomeFeedResponse | null;
    drawOutcome: IMarketOutcomeFeedResponse | null;
    awayOutcome: IMarketOutcomeFeedResponse | null;
}

const collectOutcomesForBCFeed = (
    outcomes: IMarketOutcomeFeedResponse[],
    homeTeamName?: string,
    awayTeamName?: string,
): IMainOddsState => {
    let homeOutcome: IOutcomeData | null = null;
    let drawOutcome: IOutcomeData | null = null;
    let awayOutcome: IOutcomeData | null = null;

    outcomes.forEach((outcome) => {
        const outcomeName = outcome.name;

        // Check for home team
        if (homeTeamName && outcomeName === homeTeamName) {
            homeOutcome = { ...outcome, name: OutcomeMagicNumbers.HOME };
        }
        // Check for away team
        else if (awayTeamName && outcomeName === awayTeamName) {
            awayOutcome = { ...outcome, name: OutcomeMagicNumbers.AWAY };
        }
        // Everything else is draw
        else {
            drawOutcome = { ...outcome, name: OutcomeMagicNumbers.DRAW };
        }
    });

    return { homeOutcome, drawOutcome, awayOutcome };
};

const collectOutcomesForBRFeed = (outcomes: IMarketOutcomeFeedResponse[]): IMainOddsState => {
    let homeOutcome: IOutcomeData | null = null;
    let drawOutcome: IOutcomeData | null = null;
    let awayOutcome: IOutcomeData | null = null;

    outcomes.forEach((outcome) => {
        // Standard Feed specific outcome logic
        if (outcome.outcome_id === 2) {
            drawOutcome = { ...outcome, name: OutcomeMagicNumbers.DRAW };
        } else if (!homeOutcome) {
            homeOutcome = { ...outcome, name: OutcomeMagicNumbers.HOME };
        } else if (!awayOutcome) {
            awayOutcome = { ...outcome, name: OutcomeMagicNumbers.AWAY };
        }
    });

    return { homeOutcome, drawOutcome, awayOutcome };
};

const SportsWithOutcomesCount3 = [
    SportTypes.SOCCER,
    SportTypes.ICE_HOCKEY,
    SportTypes.HANDBALL,
    SportTypes.FUTSAL,
    SportTypes.BANDY,
    SportTypes.FIELD_HOCKEY,
];

const SportsWithOutcomesCount3BC = [
    SportTypesBC.SOCCER,
    SportTypesBC.ICE_HOCKEY,
    SportTypesBC.HANDBALL,
    SportTypesBC.FUTSAL,
    SportTypesBC.BANDY,
    SportTypesBC.FIELD_HOCKEY,
    SportTypesBC.RUGBY_LEAGUE,
    SportTypesBC.RUGBY_SEVENS,
    SportTypesBC.RUGBY_UNION,
];

const getSportsWith3 = (isBCFeed: boolean) =>
    isBCFeed ? SportsWithOutcomesCount3BC : SportsWithOutcomesCount3;

const isOutcomeShallowEqual = (
    a: IMarketOutcomeFeedResponse | null | undefined,
    b: IMarketOutcomeFeedResponse | null | undefined,
): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
        a.id === b.id &&
        a.outcome_id === b.outcome_id &&
        a.void_factor === b.void_factor &&
        a.odds === b.odds &&
        a.status === b.status &&
        a.name === b.name
    );
};

type IMainMarketMeta = {
    id: number | null;
    name: string;
    status: MarketStatus | undefined;
    columnCount: number;
};

export const useMainMarketOdds = (
    sportId: number,
    markets: IMarketFeedResponse[],
    isBCFeed: boolean,
    homeTeamName?: string,
    awayTeamName?: string,
) => {
    const [marketMeta, setMarketMeta] = useState<IMainMarketMeta>({
        id: null,
        name: "",
        status: undefined,
        columnCount: getColumnCount(getSportsWith3(isBCFeed).includes(sportId) ? 3 : 2),
    });
    const [mainOdds, setMainOdds] = useState<IMainOddsState>();

    const mainMarketTemplateId = useMemo(
        () =>
            isBCFeed
                ? MainMarketsMapBC[sportId] || []
                : MainMarketsMap[sportId] || TemplateIdsMap.WINNER,
        [isBCFeed, sportId],
    );

    useEffect(() => {
        const found = findMainMarket(markets, mainMarketTemplateId);
        const outcomesCount =
            found?.outcomes_count ?? (getSportsWith3(isBCFeed).includes(sportId) ? 3 : 2);
        const columnCount = getColumnCount(outcomesCount);

        setMarketMeta((prev) => {
            const id = found?.id ?? null;
            const name = found?.name || "";
            const status = found?.status;
            if (
                prev.id === id &&
                prev.name === name &&
                prev.status === status &&
                prev.columnCount === columnCount
            ) {
                return prev;
            }
            return { id, name, status, columnCount };
        });

        // Compute raw odds directly from `found` — no stale closure on marketMeta state.
        const raw: IMainOddsState = found
            ? isBCFeed
                ? collectOutcomesForBCFeed(found.outcomes, homeTeamName, awayTeamName)
                : collectOutcomesForBRFeed(found.outcomes)
            : { homeOutcome: null, drawOutcome: null, awayOutcome: null };

        // Stabilize each outcome ref independently — only replace the ref that actually changed.
        setMainOdds((prev) => {
            const home = isOutcomeShallowEqual(prev?.homeOutcome, raw.homeOutcome)
                ? (prev?.homeOutcome ?? null)
                : raw.homeOutcome;
            const draw = isOutcomeShallowEqual(prev?.drawOutcome, raw.drawOutcome)
                ? (prev?.drawOutcome ?? null)
                : raw.drawOutcome;
            const away = isOutcomeShallowEqual(prev?.awayOutcome, raw.awayOutcome)
                ? (prev?.awayOutcome ?? null)
                : raw.awayOutcome;

            // All refs unchanged → return prev so React skips the re-render entirely.
            if (
                home === prev?.homeOutcome &&
                draw === prev?.drawOutcome &&
                away === prev?.awayOutcome
            ) {
                return prev!;
            }

            return { homeOutcome: home, drawOutcome: draw, awayOutcome: away };
        });
    }, [markets, mainMarketTemplateId, isBCFeed, homeTeamName, awayTeamName, sportId]);

    return {
        mainOdds,
        mainMarketName: marketMeta.name,
        mainMarketStatus: marketMeta.status,
        mainMarketId: marketMeta.id,
        columnCount: marketMeta.columnCount,
    };
};
